/**
 * Unified Theme Palette & Visual Tokens (#1157).
 *
 * Central source-of-truth for client colors across both Pixi/Three.js numeric
 * formats (`0xRRGGBB`) and DOM/CSS string formats (`#rrggbb`).
 *
 * Keeps visual palettes synchronized across rendering technologies without
 * manual comment-based duplication.
 */

import type { ClassColorKey } from "@pit/protocol";

// ─── Format Conversion Helpers ──────────────────────────────────────────────

/**
 * Convert a 24-bit numeric hex color (e.g. `0x3aa675`) to a lowercase zero-padded
 * `#rrggbb` CSS string (e.g. `"#3aa675"`).
 */
export function cssHex(color: number): string {
  const clamped = Math.max(0, Math.min(0xffffff, Math.trunc(color)));
  return `#${clamped.toString(16).padStart(6, "0")}`;
}

/**
 * Convert a CSS hex string (e.g. `"#3aa675"` or `"#333"`) to a 24-bit integer.
 */
export function numHex(hex: string): number {
  const clean = hex.replace(/^#/u, "").trim();
  if (clean.length === 3) {
    const r = clean.charAt(0);
    const g = clean.charAt(1);
    const b = clean.charAt(2);
    return Number.parseInt(`${r}${r}${g}${g}${b}${b}`, 16);
  }
  return Number.parseInt(clean, 16);
}

// ─── Class & Champion Palettes ──────────────────────────────────────────────

/**
 * Canonical WoW-inspired class color palette in Pixi numeric format (`0xRRGGBB`).
 * Pinned by `CLASS_COLOR_KEY_SCHEMA` from `@pit/protocol`.
 */
export const CLASS_COLORS = {
  deathKnight: 0xc41e3a,
  demonHunter: 0xa330c9,
  druid: 0xff7c0a,
  evoker: 0x33937f,
  hunter: 0xaad372,
  mage: 0x3fc7eb,
  monk: 0x00ff98,
  paladin: 0xf48cba,
  priest: 0xffffff,
  rogue: 0xfff569,
  shaman: 0x0070dd,
  warlock: 0x8788ee,
  warrior: 0xc69b6d,
} as const satisfies Record<ClassColorKey, number>;

export const CHAMPION_COLOR_NEUTRAL = 0x888888;

// ─── Resource Palettes ──────────────────────────────────────────────────────

/** Resource bar fills (numeric). */
export const RESOURCE_COLOR_RAGE = 0xc0392b;
export const RESOURCE_COLOR_ENERGY = 0xf1c40f;
export const RESOURCE_COLOR_MANA = 0x2f7ed8;
export const RESOURCE_COLOR_UNKNOWN = 0xb28dd6;

export const RESOURCE_COLORS: Readonly<Record<string, number>> = {
  rage: RESOURCE_COLOR_RAGE,
  energy: RESOURCE_COLOR_ENERGY,
  mana: RESOURCE_COLOR_MANA,
};

// ─── Team & Projectile Palettes ─────────────────────────────────────────────

export const TEAM_0_COLOR = 0x3ec6e0;
export const TEAM_1_COLOR = 0xe0563e;
export const UNKNOWN_TEAM_COLOR = 0x888888;

export const OWN_PROJECTILE_COLOR = 0x65d1e6;
export const ENEMY_PROJECTILE_COLOR = 0xe67865;

// ─── HUD & Bar Palettes ─────────────────────────────────────────────────────

export const HP_BAR_COLOR = 0x3aa675;
export const HP_BAR_FILL_COLOR = cssHex(HP_BAR_COLOR);

export const SHIELD_BAR_COLOR = 0xb8c0c8;
export const SHIELD_BAR_FILL_COLOR = cssHex(SHIELD_BAR_COLOR);

export const HP_BAR_BG = 0x161d24;
export const BAR_BORDER = 0x000000;

export const CAST_BAR_COLOR = 0xf1c40f;
export const CAST_BAR = CAST_BAR_COLOR;
export const CAST_BAR_FILL_COLOR = "#d5a640"; // DOM frame cast accent

export const POINTS_PIP_FILLED = 0xc9a63a;
export const POINTS_PIP_FILLED_CSS = cssHex(POINTS_PIP_FILLED);

export const POINTS_PIP_EMPTY = 0x333333;
export const POINTS_PIP_EMPTY_CSS = cssHex(POINTS_PIP_EMPTY);

// ─── World & Arena Palettes ─────────────────────────────────────────────────

export const BG_COLOR = 0x0b0f14;
export const ARENA_BORDER_COLOR = 0x2a3947;
export const SPAWN_MARKER_COLOR = 0x4a6a80;

export const PILLAR_SIDE_COLOR = 0x2b2823;
export const PILLAR_TOP_COLOR = 0x4a443b;
export const PILLAR_RIM_COLOR = 0x8c7e68;
export const PILLAR_SHADOW_COLOR = 0x000000;
export const EDGE_WALL_COLOR = 0x3a3630;

export const BODY_OUTLINE = 0x0a1520;
export const FACING_TICK = 0xf0f0f0;
export const WALL_COLOR = 0xffffff;

/**
 * Return the team color for a team index (0 or 1).
 */
export function teamColor(team: number): number {
  if (team === 0) return TEAM_0_COLOR;
  if (team === 1) return TEAM_1_COLOR;
  return UNKNOWN_TEAM_COLOR;
}